import { Box, Checkbox, Typography } from '@material-ui/core'
import { CustomTooltip } from 'components/facets'
import { LanguageFlag } from 'components/facets/LanguageFlag'
import { useFillViewportHeight, useOrderStore } from 'lib/hooks'
import { getGameCodeFromName, truncateText } from 'lib/utils'
import { useEffect, useRef } from 'react'
import { AutoSizer, Column, Index, Table } from 'react-virtualized'
import { getOrderState, Order, orderContainsSealed } from 'shared'
import { BACKOFFICE_ORDERS_TABLE_HEIGHT } from 'utils/constants'
import { StatusLabel } from './StatusLabel'

export interface CompactOrdersTableProps {
  handleSelectRow: (e: any, order: Order) => void
  viewOnly?: boolean
}

export const CompactOrdersTable = ({
  handleSelectRow,
  viewOnly,
}: CompactOrdersTableProps): JSX.Element => {
  const {
    filteredOrders,
    activeTab,
    expandedOrder,
    expandedRowIndex,
    selectedOrderIds,
    setExpandedOrder,
    setExpandedRowIndex,
  } = useOrderStore()

  const MAX_CHARS = 20
  // Below this width the columns get illegible on narrow (tablet) panels, so the table scrolls
  // horizontally instead of cramming — mirrors the main orders table's Math.max(width, ...).
  const MIN_TABLE_WIDTH = 620

  const getFlagFromCountry = (country: string | undefined) => {
    if (country === 'D') {
      country = 'DE'
    }

    return <LanguageFlag height="10px" code={country || 'gg'} style={{ alignSelf: 'center' }} />
  }

  const handleExpandRow = (order: Order, rowIndex: number) => {
    setExpandedOrder(order)
    setExpandedRowIndex(rowIndex)
  }

  const handleReduceRow = () => {
    setExpandedOrder(undefined)
    setExpandedRowIndex(undefined)
  }

  // Fill the viewport below the compact table instead of the old `windowHeight - 300` magic.
  const [tableContainerRef, tableHeight] = useFillViewportHeight<HTMLDivElement>([
    activeTab,
    filteredOrders.length,
  ])

  // Keep the row the user opened in view when the compact table appears (or when they open a
  // different row). react-virtualized mounts scrolled to the top, so without this the opened
  // row is off-screen and the list looks like its scroll reset. Runs only when the opened row
  // changes, so it never fights the user's own scrolling; rAF waits for AutoSizer to measure.
  const tableRef = useRef<Table>(null)
  useEffect(() => {
    if (expandedRowIndex === undefined) return
    const raf = requestAnimationFrame(() => tableRef.current?.scrollToRow(expandedRowIndex))
    return () => cancelAnimationFrame(raf)
  }, [expandedRowIndex])

  return (
    <div className="responsive-table-container">
      <div ref={tableContainerRef} />
      <AutoSizer disableHeight>
        {({ width }) => (
          <Table
            ref={tableRef}
            width={Math.max(width, MIN_TABLE_WIDTH)}
            height={viewOnly ? BACKOFFICE_ORDERS_TABLE_HEIGHT : tableHeight}
            headerHeight={30}
            rowHeight={45}
            scrollToAlignment="center"
            rowCount={filteredOrders.length}
            rowGetter={({ index }) => filteredOrders[index]}
            rowClassName={(info: Index) => {
              if (info.index === expandedRowIndex) {
                return 'selected-row'
              }
              return ''
            }}
          >
            {activeTab === 1 && (
              <Column
                width={48}
                flexShrink={0}
                label="Select"
                dataKey=""
                headerClassName="centered-header"
                cellRenderer={({ rowData: order }) => (
                  <Checkbox
                    checked={selectedOrderIds.includes(order.ptOrderId)}
                    disabled={getOrderState(order) === 'picking'}
                    onClick={(e: any) => handleSelectRow(e, order)}
                  />
                )}
              />
            )}

            <Column
              width={80}
              flexGrow={2}
              flexShrink={1}
              label="Status"
              dataKey=""
              headerClassName="centered-header"
              cellRenderer={({ rowData: order }) => (
                <div className="d-flex align-items-center justify-content-center">
                  <StatusLabel
                    type="state"
                    name={getOrderState(order)}
                    state={getOrderState(order)}
                    active={true}
                  ></StatusLabel>
                </div>
              )}
            />

            <Column
              width={30}
              flexShrink={0}
              label=""
              dataKey=""
              cellRenderer={({ rowData: order }) => (
                <>
                  <CustomTooltip title={order.articleGames}>
                    <span
                      className={`ss-${getGameCodeFromName(order.articleGames)} ss-game-icon ss-fw`}
                      style={{ fontSize: '1.5rem' }}
                    ></span>
                  </CustomTooltip>
                </>
              )}
            />

            <Column
              width={34}
              flexShrink={0}
              label=""
              dataKey=""
              disableSort={true}
              cellRenderer={({ rowData: order }) => (
                <>
                  {orderContainsSealed(order) && (
                    <CustomTooltip title="Contains sealed">
                      <i className="fa-light fa-box-taped orders-table-informative-icon"></i>
                    </CustomTooltip>
                  )}
                </>
              )}
            />

            <Column
              width={120}
              flexGrow={4}
              flexShrink={1}
              label="Buyer"
              dataKey=""
              cellRenderer={({ rowData: order }) => (
                <>
                  <Box display="flex">
                    {getFlagFromCountry(order.shipping?.country)}
                    <Box paddingLeft={1}>
                      <Box>{truncateText(order.buyer?.username ?? '', MAX_CHARS)}</Box>
                      <Box>{order.buyer?.address?.name}</Box>
                    </Box>
                  </Box>
                </>
              )}
            />

            {[1, 3].includes(activeTab) && (
              <Column
                width={64}
                flexGrow={1}
                flexShrink={1}
                label="Order Bin"
                dataKey="order-bin"
                headerClassName="centered-header"
                cellRenderer={({ rowData: order }) => (
                  <div className="d-flex align-items-center justify-content-center">
                    {order.picking && order.picking.pickingId ? order.picking.pickingId : '-'}
                  </div>
                )}
              />
            )}

            <Column
              width={84}
              flexGrow={2}
              flexShrink={1}
              label=""
              dataKey=""
              cellRenderer={({ rowData: order, rowIndex }) => (
                <>
                  {order.ptOrderId === expandedOrder?.ptOrderId ? (
                    <Box onClick={() => handleReduceRow()} style={{ cursor: 'pointer' }}>
                      <div className="d-flex align-items-center orders-table-order">
                        <Typography style={{ fontSize: '0.75rem' }}>
                          {order.sourceOrderId}
                        </Typography>
                        <i className="fa-regular fa-chevron-left orders-table-order-icon"></i>
                      </div>
                    </Box>
                  ) : (
                    <Box
                      onClick={() => handleExpandRow(order, rowIndex)}
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="d-flex align-items-center orders-table-order">
                        <Typography style={{ fontSize: '0.75rem' }}>
                          {order.sourceOrderId}
                        </Typography>
                        <i className="fa-regular fa-chevron-right orders-table-order-icon"></i>
                      </div>
                    </Box>
                  )}
                </>
              )}
            />
          </Table>
        )}
      </AutoSizer>
    </div>
  )
}
